'use client';

import type L from 'leaflet';
import type React from 'react';
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDebounceCallback } from 'usehooks-ts';

import { isEqual, isNil } from 'lodash-es';
import type { PartnerResultDescriptorType } from '../../../proxies/SearchProxy';
import { searchPartnersRequest } from '../../../redux/ducks/SearchDucks';
import type { SearchPartnersRequestType } from '../../../redux/ducks/SearchDucks.types';
import { setSearchSelection } from '../../../redux/ducks/StorageDucks';
import type { SearchSelectionType } from '../../../redux/ducks/StorageDucks.types';
import { setCurrentlySelectedMapItem } from '../../../redux/ducks/UIDucks';
import type { RootReducerType } from '../../../redux/reducers/RootReducer.types';
import { trackEvent } from '../../../utils/Umami/Umami';
import ProfessionalCard from '../../layout/ProfessionalCard/ProfessionalCard';
import { convertToSearchType } from './WelcomeBody';

import { lazyReactNaiveRetry } from '../../../constants/Lazy';
import type { MarkerProps } from '../../layout/Map';
const CustomMap = lazyReactNaiveRetry(() => import('../../layout/Map'));

const DEBOUNCE_MAP_MOVE_TIME = 750; // in milliseconds

export type WelcomeListViewProps = {
  onPartnerSelected: (partner: PartnerResultDescriptorType) => void;
};

const WelcomeMapView: React.FC<WelcomeListViewProps> = ({
  onPartnerSelected,
}) => {
  const dispatch = useDispatch();

  const searchPartnersResponse = useSelector(
    (state: RootReducerType) => state.search.searchPartnersResponse?.data,
  );
  const searchSelection = useSelector(
    (state: RootReducerType) => state.storage.searchSelection,
  );
  const listMapItems = useSelector(
    (state: RootReducerType) => state.ui.listMapItems,
  );
  const [hasSelectionChanged, setSelectionChanged] = useState<number>(0);
  const [onAutoFit, setOnAutoFit] = useState<boolean>(true);
  const [currentMapBounds, setCurrentMapBounds] = useState<
    L.LatLngBounds | undefined
  >();
  const [lastSearchParams, setLastSearchParams] = useState<
    SearchPartnersRequestType | undefined
  >();
  const [openMarkers, setOpenMarkers] = useState<string[]>([]);

  const searchSel = useMemo<SearchSelectionType | undefined>(() => {
    if (!isNil(searchSelection)) {
      return searchSelection;
    }

    return undefined;
  }, [searchSelection]);

  const onPartnerMarker = useCallback(
    (partnerId: string) => {
      trackEvent('map_search_marker_selected', {
        partner: `partner-${partnerId}`,
      });

      const selectedPartners: PartnerResultDescriptorType[] =
        listMapItems.filter((i) => i.id === partnerId);
      if (selectedPartners.length > 0) {
        dispatch(setCurrentlySelectedMapItem(selectedPartners[0]));
      }
    },
    [listMapItems],
  );

  const onMapClick = useCallback((event: any) => {
    dispatch(setCurrentlySelectedMapItem(undefined));
  }, []);

  const onMapMoveEnd = useCallback((bounds: L.LatLngBounds) => {
    setCurrentMapBounds(bounds);
  }, []);

  const debouncedMapMoveEnd = useDebounceCallback(
    onMapMoveEnd,
    DEBOUNCE_MAP_MOVE_TIME,
    { trailing: true },
  );

  const mapBounds = useMemo(() => currentMapBounds, [currentMapBounds]);

  const allItems = useMemo<PartnerResultDescriptorType[]>(() => {
    if (searchPartnersResponse) {
      return [...searchPartnersResponse.results];
    }

    return [];
  }, [searchPartnersResponse]);

  const selectedLocation = useMemo(
    () => searchSel?.placeLocation,
    [searchSel?.placeLocation],
  );

  // TODO: This should be a useState, checking first if the old and the new are different
  const markers = useMemo<MarkerProps[]>(() => {
    const result: MarkerProps[] = [];
    const input: PartnerResultDescriptorType[] =
      hasSelectionChanged > 1 ? [...allItems] : [...listMapItems];

    for (let index = 0; index < input.length; index++) {
      const item = input[index];
      const marker = {
        id: item.id,
        active: true,
        lat: item.location.latitude,
        lng: item.location.longitude,
      };

      result.push(marker);
    }

    return result;
  }, [listMapItems, allItems, hasSelectionChanged]);

  const center = useMemo<number[]>(() => {
    let totLat = 0.0;
    let totLng = 0.0;

    for (let index = 0; index < markers.length; index++) {
      const mkr = markers[index];
      totLat += mkr.lat;
      totLng += mkr.lng;
    }

    return [totLat / markers.length, totLng / markers.length];
  }, [markers]);

  useEffect(() => {
    if (searchSel) {
      const params = convertToSearchType(searchSel, 0);
      if (!isEqual(params, lastSearchParams)) {
        setLastSearchParams(params); // should be set only if changed
      }
    }
  }, [searchSel]);

  useEffect(() => {
    if (lastSearchParams) {
      dispatch(searchPartnersRequest(lastSearchParams));
      setSelectionChanged(hasSelectionChanged + 1);
    }
  }, [lastSearchParams]);

  useEffect(() => {
    if (selectedLocation) {
      setOnAutoFit(true);
    }
  }, [selectedLocation]);

  useEffect(() => {
    if (mapBounds && searchSel) {
      if (onAutoFit) {
        setOnAutoFit(false);
      }

      if (!onAutoFit && openMarkers.length === 0) {
        dispatch(
          setSearchSelection({
            ...searchSel,
            bounds: [
              mapBounds.getNorthEast().lat,
              mapBounds.getNorthEast().lng,
              mapBounds.getSouthWest().lat,
              mapBounds.getSouthWest().lng,
            ],
          }),
        );
      }
    }
  }, [mapBounds]);

  useEffect(
    () => () => {
      if (searchSel) {
        dispatch(
          setSearchSelection({
            ...searchSel,
            bounds: undefined,
          }),
        );
      }
    },
    [],
  );

  const onVisibilityChange = useCallback(
    ({
      isVisible,
      desc,
    }: {
      isVisible: boolean;
      desc: PartnerResultDescriptorType;
    }) => {
      if (!isVisible) {
        setOpenMarkers((m) => {
          const result = m.filter((x) => x !== desc.id);

          return result;
        });
      } else {
        setOpenMarkers((m) => {
          const result = m.filter((x) => x !== desc.id);
          result.push(desc.id);

          return result;
        });
      }
    },
    [openMarkers],
  );

  const markerPopup = (descriptor: MarkerProps) => {
    const input: PartnerResultDescriptorType[] =
      hasSelectionChanged > 1 ? [...allItems] : [...listMapItems];
    const selectedMarkerDesc: PartnerResultDescriptorType[] = input.filter(
      (i) => i.id === descriptor.id,
    );

    if (selectedMarkerDesc.length > 0) {
      const desc = selectedMarkerDesc[0];

      return (
        <ProfessionalCard
          partnerDescriptor={desc}
          partnerIndex={0}
          data-test-id={`professional-test-id-${desc.id}`}
          onClick={() => onPartnerSelected(desc)}
          onVisibilityChange={({ isVisible }) =>
            onVisibilityChange({ isVisible, desc })
          }
        />
      );
    }

    return null;
  };

  return (
    <Suspense fallback={<div className="hidden">&nbsp;</div>}>
      <div className="h-[calc(100dvh)] w-screen">
        <CustomMap
          id="search_view_map"
          styled=""
          readOnly={false}
          zoomControl={false}
          scrollWheelZoom
          markers={markers}
          autoFit={onAutoFit}
          onMarkerClick={onPartnerMarker}
          onMapClick={onMapClick}
          onMoveEnd={debouncedMapMoveEnd}
          markerPopup={markerPopup}
          center={center}
        />
      </div>
    </Suspense>
  );
};

export default WelcomeMapView;
